import type { BatchView } from './ComplaintBatch';
// Consumer complaints (migration 042): the shapes GET /complaints and GET /complaints/{id} return,
// and the small pieces of wording both screens use.

export type Severity = 'High' | 'Medium' | 'Low' | 'None';
/** MAILBOX (047): what the feedback@ mailbox showed, read by the app; it names nobody. */
export type Via = 'APP' | 'MIRROR' | 'MAILBOX';

export interface Attested<T> {
  value: T;
  from: Via;
  byUpn: string | null;
  at: string;
}

export interface ComplaintView {
  id: number;
  caseNo: string;
  source: 'AC' | 'HAND';
  acDealId: number | null;
  acDealUrl: string | null;
  channel: string;
  receivedAt: string;
  consumerName: string | null;
  country: string | null;
  placeOfPurchase: string | null;
  product: string | null;
  productSize: string | null;
  batchCode: string | null;
  bestBefore: string | null;
  message: string | null;
  photos: string[];
  recordedByUpn: string;
  stage: string;
  stageLabel: string;
  severity: Attested<Severity> | null;
  category: Attested<string> | null;
  finding: Attested<string> | null;
  owner: { upn: string; at: string } | null;
  acknowledgedAt: string | null;
  acknowledgedVia: Via | null;
  replies: number;
  lastReplyAt: string | null;
  closed: { at: string; from: Via; byUpn: string | null; note: string | null } | null;
  voucher: number | null;
  clocks: { ackDueAt: string; ackMet: boolean; ackLateHours: number | null; closeDays: number; closeDueAt: string; closeOverDays: number };
  notInAc: string[];
  sameBatch: number[];
  /** The D365 batch the case is about (rule 36); absent from an older API, null before it is resolved. */
  batch?: BatchView | null;
  /** The latest stored trace's counts (051), of the batch in force; null when not traced yet. */
  trace?: { lines: number; flagged: number; ncrs: number | null; onPaper: number; at: string } | null;
}

/** The phone's two tabs on a case (UI/UX pass, 3 Nov 2026): the work, and the record. In the URL, so a link keeps it. */
export const CASE_TABS = [
  { id: 'act', label: 'Act' },
  { id: 'details', label: 'Details' },
] as const;
export type CaseTab = (typeof CASE_TABS)[number]['id'];
export const caseTab = (v: string | null | undefined): CaseTab => (v === 'details' ? 'details' : 'act');

export interface ComplaintEvent {
  id: number;
  eventType: string;
  via: Via;
  severity: Severity | null;
  category: string | null;
  stage: string | null;
  text: string | null;
  payload: Record<string, unknown> | null;
  byUpn: string | null;
  at: string;
}

export interface ComplaintsConfig {
  enabled: boolean;
  /** Week 2 (Amit 31 Oct 2026): this person is in QUALITY_COMPLAINT_PILOT. */
  pilot?: boolean;
  acConfigured: boolean;
  myGrants: string[];
  severities: Severity[];
  /** SOP v14 Table 2 as text (api domain/sopTable2.ts). */
  severityTable?: { rows: Record<'High' | 'Medium' | 'Low', { type: string; examples: string }[]>; note: string };
  categories: string[];
  stages: Array<{ key: string; label: string }>;
  channels: string[];
  closeDays: Record<Severity, number>;
  /** 047: the reply templates. */
  templates?: Array<{ key: string; label: string }>;
}

export const SEVERITY_TONE: Record<Severity, string> = {
  High: 'bg-(--out-bg) text-(--out-fg)',
  Medium: 'bg-(--action-bg) text-(--action-fg)',
  Low: 'bg-(--subtle) text-(--fg)',
  None: 'bg-(--subtle) text-(--muted)',
};

export const CHANNEL_LABEL: Record<string, string> = {
  FORM: 'Feedback form',
  EMAIL: 'Email',
  PHONE: 'Phone',
  LETTER: 'Letter',
  SOCIAL: 'Social media',
  RETAILER: 'Retailer',
  OTHER: 'Other',
};

export const EVENT_LABEL: Record<string, string> = {
  AC_OBSERVED: 'ActiveCampaign changed',
  TAKEN: 'Took the case',
  ACKNOWLEDGED: 'Acknowledged',
  REPLIED: 'Email to the consumer',
  SEVERITY_SET: 'Severity set',
  FINDING_RECORDED: 'Finding recorded',
  STAGE_SET: 'Stage',
  NOTE: 'Note',
  CLOSED: 'Closed',
  REOPENED: 'Reopened',
  REPLY_APPROVED: 'Reply approved',
  CONSUMER_REPLIED: 'Email from the consumer',
};

const BRISBANE = 'Australia/Brisbane';
export function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-AU', { timeZone: BRISBANE, day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
}
export function day(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-AU', { timeZone: BRISBANE, day: 'numeric', month: 'short', year: 'numeric' });
}

/** Who did it, in words: a person here, or AC's shared login (which names nobody). */
export function byWhom(from: Via, upn: string | null): string {
  if (from === 'MIRROR') return 'in ActiveCampaign';
  if (from === 'MAILBOX') return 'from feedback@ in Outlook';
  return upn ? upn.split('@')[0].replace(/^./, (c) => c.toUpperCase()) : 'unknown';
}

/** Hours as a person reads them: hours up to two days, whole days after ("ack 2755 h late" said nothing). */
export function hoursText(h: number): string {
  return h < 48 ? `${h} h` : `${Math.floor(h / 24)} d`;
}

/** The clock, as one short phrase and a tone. */
export function clockPhrase(c: ComplaintView): { text: string; tone: 'bad' | 'warn' | 'ok' | 'muted' } {
  if (c.closed) {
    const over = c.clocks.closeOverDays;
    return over > 0 ? { text: `closed ${over} d late`, tone: 'warn' } : { text: 'closed in time', tone: 'ok' };
  }
  if (!c.clocks.ackMet) {
    const h = c.clocks.ackLateHours ?? 0;
    return h >= 0 ? { text: `ack ${hoursText(h)} late`, tone: 'bad' } : { text: `ack due in ${hoursText(-h)}`, tone: 'warn' };
  }
  const over = c.clocks.closeOverDays;
  if (over > 0) return { text: `${over} d over`, tone: 'bad' };
  return { text: `close in ${-over} d`, tone: over > -4 ? 'warn' : 'muted' };
}

export function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Week 2 (migration 043): Claude's reading of a case, for the pilot's people only. A suggestion, never the record. */
export interface PilotSuggestion {
  category: string | null;
  severity: Severity | null;
  product: string | null;
  batchCode: string | null;
  template: string;
  templateLabel: string;
  triggers: Array<{ trigger: string; why: string; label: string }>;
  s131: boolean;
  reasoning: string | null;
  draftReply: string | null;
  model: string;
  policyVersion: string;
  at: string;
}

export interface PilotMessage {
  id: number;
  /** AC's log, feedback@ read by Graph, or sent here (047). */
  source?: 'AC' | 'GRAPH' | 'APP';
  outbound: boolean;
  automated: boolean;
  from: string | null;
  subject: string | null;
  body: string | null;
  sentAt: string;
  sentByUpn?: string | null;
  template?: string | null;
  policyVersion?: string | null;
}

export interface PilotPayload {
  triageOn: boolean;
  messages: PilotMessage[];
  photos: Array<{ photoId: number; sourceUrl: string; contentType: string }>;
  cards: string;
  suggestion: PilotSuggestion | null;
  /** 047: is feedback@ read, and is sending from it on (off, on, or test to one person). */
  mail?: { read: boolean; replies: 'off' | 'on' | 'test' };
  /** 047: the reply rules on this case. */
  replies?: {
    triggers: Array<{ trigger: string; label: string }>;
    flagged: boolean;
    withheld: boolean;
    latestDraft: { suggestionId: number; template: string; text: string | null; reasoning: string | null; policyVersion: string; byUpn: string | null; at: string } | null;
    approvals: Array<{ id: number; template: string; textHash: string; approvedByUpn: string; at: string }>;
    awaitingUs: boolean;
    hasRecipient: boolean;
    /** Test mode: every send goes to this person instead of the consumer. */
    test: string | null;
  };
}
